import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, Heart, Shield, Users, Activity, Mail, Phone, MapPin, Award, Building, User } from 'lucide-react';
import Select from 'react-select';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useHospitals, useHealthZones } from '../../hooks/useApi';

const RegisterDoctor = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    specialty: '',
    hospital: '',
    zone: '',
    licenseNumber: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Charger les données depuis les API
  const { data: hospitals = [], loading: hospitalsLoading } = useHospitals();
  const { data: healthZones = [], loading: healthZonesLoading } = useHealthZones();

  // Liste des spécialités médicales
  const specialties = [
    { value: 'Cardiologie', label: 'Cardiologie' },
    { value: 'Pédiatrie', label: 'Pédiatrie' },
    { value: 'Médecine générale', label: 'Médecine générale' },
    { value: 'Gynécologie', label: 'Gynécologie' },
    { value: 'Obstétrique', label: 'Obstétrique' },
    { value: 'Chirurgie générale', label: 'Chirurgie générale' },
    { value: 'Chirurgie orthopédique', label: 'Chirurgie orthopédique' },
    { value: 'Dermatologie', label: 'Dermatologie' },
    { value: 'Neurologie', label: 'Neurologie' },
    { value: 'Psychiatrie', label: 'Psychiatrie' },
    { value: 'Ophtalmologie', label: 'Ophtalmologie' },
    { value: 'ORL (Oto-rhino-laryngologie)', label: 'ORL (Oto-rhino-laryngologie)' },
    { value: 'Urologie', label: 'Urologie' },
    { value: 'Radiologie', label: 'Radiologie' },
    { value: 'Anesthésie', label: 'Anesthésie' },
    { value: 'Médecine d\'urgence', label: 'Médecine d\'urgence' },
    { value: 'Médecine interne', label: 'Médecine interne' },
    { value: 'Endocrinologie', label: 'Endocrinologie' },
    { value: 'Gastro-entérologie', label: 'Gastro-entérologie' },
    { value: 'Pneumologie', label: 'Pneumologie' },
    { value: 'Rhumatologie', label: 'Rhumatologie' },
    { value: 'Oncologie', label: 'Oncologie' },
    { value: 'Hématologie', label: 'Hématologie' },
    { value: 'Néphrologie', label: 'Néphrologie' },
    { value: 'Médecine tropicale', label: 'Médecine tropicale' },
    { value: 'Médecine du travail', label: 'Médecine du travail' },
    { value: 'Médecine légale', label: 'Médecine légale' },
    { value: 'Autre', label: 'Autre' }
  ];

  // Préparer les options pour les hôpitaux
  const hospitalOptions = hospitals.map(hospital => ({
    value: hospital.id,
    label: hospital.name || hospital.nom || 'Hôpital sans nom'
  }));

  // Préparer les options pour les zones de santé
  const healthZoneOptions = healthZones.map(zone => ({
    value: zone.id,
    label: `${zone.name || zone.nom || 'Zone sans nom'}${zone.code ? ` (${zone.code})` : ''}${zone.province ? ` - ${zone.province}` : ''}`
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer les erreurs lors de la saisie
    if (error) setError('');
  };

  const handleSpecialtyChange = (selectedOption) => {
    setSelectedSpecialty(selectedOption);
    setFormData(prev => ({
      ...prev,
      specialty: selectedOption ? selectedOption.value : ''
    }));
    // Effacer les erreurs lors de la sélection
    if (error) setError('');
  };

  const handleHospitalChange = (selectedOption) => {
    setSelectedHospital(selectedOption);
    if (selectedOption) {
      const hospital = hospitals.find(h => h.id === selectedOption.value);
      setFormData(prev => ({
        ...prev,
        hospital: hospital ? (hospital.name || hospital.nom || '') : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        hospital: ''
      }));
    }
  };

  const handleZoneChange = (selectedOption) => {
    setSelectedZone(selectedOption);
    if (selectedOption) {
      const zone = healthZones.find(z => z.id === selectedOption.value);
      setFormData(prev => ({
        ...prev,
        zone: zone ? (zone.name || zone.nom || '') : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        zone: ''
      }));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Le nom est requis');
      return false;
    }
    if (!formData.email.trim()) {
      setError('L\'email est requis');
      return false;
    }
    if (!formData.password) {
      setError('Le mot de passe est requis');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return false;
    }
    if (!formData.specialty.trim()) {
      setError('La spécialité est requise');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        role: 'doctor',
        specialty: formData.specialty,
        hospital: formData.hospital || undefined,
        zone: formData.zone || undefined,
        licenseNumber: formData.licenseNumber || undefined
      };

      const response = await apiService.register(registerData);
      
      if (response.success) {
        setSuccess(true);
        // Connexion automatique après inscription
        setTimeout(async () => {
          try {
            await login(formData.email, formData.password);
            navigate('/dashboard');
          } catch (err) {
            // Si la connexion automatique échoue, rediriger vers la page de connexion
            navigate('/login');
          }
        }, 2000);
      } else {
        setError(response.message || 'Erreur lors de l\'inscription');
      }
    } catch (err) {
      console.error('Erreur lors de l\'inscription:', err);
      setError(err.message || 'Erreur lors de l\'inscription. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Partie gauche avec dégradé bleu */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
        {/* Formes décoratives */}
        <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full animate-pulse" />
        <div className="absolute bottom-20 left-10 w-24 h-24 bg-white/5 rounded-full" />
        <div className="absolute top-1/3 left-1/4 w-16 h-16 bg-white/10 rounded-lg" />

        {/* Contenu de bienvenue */}
        <div className="relative z-10 flex flex-col justify-center items-center text-center text-white p-12">
          <div className="mb-8">
            <Heart className="h-16 w-16 mx-auto mb-6 text-white/90" />
            <h1 className="text-4xl font-bold mb-4">
              Rejoignez HealthBridge
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Inscrivez-vous en tant que médecin et commencez à gérer vos patients
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <Shield className="h-6 w-6 text-blue-200" />
              <span className="text-blue-100">Plateforme sécurisée et certifiée</span>
            </div>
            <div className="flex items-center space-x-4">
              <Users className="h-6 w-6 text-blue-200" />
              <span className="text-blue-100">Gestion complète de vos patients</span>
            </div>
            <div className="flex items-center space-x-4">
              <Activity className="h-6 w-6 text-blue-200" />
              <span className="text-blue-100">Suivi médical en temps réel</span>
            </div>
          </div>

          <div className="mt-12 p-6 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
            <p className="text-blue-100 text-sm">
              "Rejoignez notre communauté de professionnels de santé"
            </p>
          </div>
        </div>
      </div>

      {/* Partie droite avec formulaire */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <UserPlus className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Inscription Médecin
            </h2>
            <p className="text-gray-600">
              Créez votre compte pour commencer
            </p>
          </div>

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm mb-6">
              Inscription réussie ! Redirection en cours...
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Informations personnelles */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Informations personnelles</h3>
              
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="h-4 w-4 inline mr-2" />
                  Nom complet *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  placeholder="Dr. Jean Dupont"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="h-4 w-4 inline mr-2" />
                  Adresse email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  placeholder="docteur@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="h-4 w-4 inline mr-2" />
                  Téléphone
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  placeholder="+243 81 000 0000"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe *
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    placeholder="Minimum 6 caractères"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe *
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                    placeholder="Répétez le mot de passe"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Informations professionnelles */}
            <div className="space-y-4 pt-4">
              <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Informations professionnelles</h3>
              
              <div>
                <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-2">
                  <Award className="h-4 w-4 inline mr-2" />
                  Spécialité *
                </label>
                <Select
                  id="specialty"
                  value={selectedSpecialty}
                  onChange={handleSpecialtyChange}
                  options={specialties}
                  placeholder="Sélectionner une spécialité..."
                  isSearchable
                  isClearable
                  required
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? '#3b82f6' : error && !formData.specialty ? '#ef4444' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      minHeight: '48px',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#9ca3af',
                    }),
                  }}
                />
              </div>

              <div>
                <label htmlFor="hospital" className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="h-4 w-4 inline mr-2" />
                  Hôpital
                </label>
                <Select
                  id="hospital"
                  value={selectedHospital}
                  onChange={handleHospitalChange}
                  options={hospitalOptions}
                  placeholder="Sélectionner un hôpital..."
                  isSearchable
                  isClearable
                  isLoading={hospitalsLoading}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      minHeight: '48px',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#9ca3af',
                    }),
                  }}
                />
              </div>

              <div>
                <label htmlFor="zone" className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="h-4 w-4 inline mr-2" />
                  Zone de santé
                </label>
                <Select
                  id="zone"
                  value={selectedZone}
                  onChange={handleZoneChange}
                  options={healthZoneOptions}
                  placeholder="Sélectionner une zone de santé..."
                  isSearchable
                  isClearable
                  isLoading={healthZonesLoading}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.1)' : 'none',
                      '&:hover': {
                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af',
                      },
                      minHeight: '48px',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#9ca3af',
                    }),
                  }}
                />
              </div>

              <div>
                <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  <Award className="h-4 w-4 inline mr-2" />
                  Numéro de licence
                </label>
                <input
                  id="licenseNumber"
                  name="licenseNumber"
                  type="text"
                  value={formData.licenseNumber}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  placeholder="Numéro de licence médicale"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || success}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <UserPlus className="h-5 w-5 mr-2" />
                  S'inscrire
                </>
              )}
            </button>

            <div className="text-center text-sm text-gray-600 space-y-2">
              <p>
                Vous avez déjà un compte ?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Se connecter
                </Link>
              </p>
              <p>
                Patient ?{' '}
                <Link to="/register-patient" className="text-blue-600 hover:text-blue-700 font-medium">
                  S'inscrire en tant que patient
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterDoctor;

